import { Link, useLocation } from "react-router-dom";
import classNames from "clsx";
import dayjs from "dayjs";

import { IoMdOpen } from "react-icons/io";
import { BsBell } from "react-icons/bs";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import { useAuth } from "@/modules/auth";
import { KTSVG, toAbsoluteUrl } from "@/../_metronic/helpers";
import { useWindowDimensions } from "@/hooks/useWindowDimensions";
import { TabsWithRoute } from "@/components/TabsWithRoute";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";
import { VitalSignsCard } from "@/components/VitalSignsCard";
import { ScrollableTabs } from "@/components/ScrollableTabs";

import "./styles.scss";

type Props = {
	attendance: Attendance;
	skeleton?: boolean;
};

type PathToTabObject = {
	[key: string]: string | boolean | undefined;
};

export function PatientProfile({ attendance, skeleton }: Props) {
	const location = useLocation();
	const { width } = useWindowDimensions();
	const { currentAuth } = useAuth();
	const user = currentAuth?.user;

	const surgicalAttendance = attendance.surgicalAttendance;
	const latestMetrics = attendance.latestMetrics;
	const hasFilledHospitalizationPermit = Boolean(attendance.activeHospitalizationPermit);

	const activeTab = activeTabByPathname(location.pathname);
	const pathSafeSurgery = setPathSafeSurgery();

	function activeTabByPathname(pathname: string) {
		const pathToTabObject: PathToTabObject = {
			"pre-operatorio": "before-operatory",
			"sala-de-operacao": "operating-room",
			"sala-de-recuperacao-pos-anestesica": "post-anesthetic-recovery-room",
			"cirurgia-segura": "safe-surgery",
		};

		const pathKeys = Object.keys(pathToTabObject);
		const matchingPath = pathKeys.find((key) => pathname.includes(key));

		if (matchingPath) {
			return pathToTabObject[matchingPath];
		}

		return "";
	}

	function setPathSafeSurgery() {
		const paths: PathToTabObject = {
			"pre-inducao-anestesica": user?.can("View-Pre-Anesthetic-Induction"),
			"pre-cirurgia": user?.can("View-Pre-Surgery"),
			"pre-sala-de-recuperacao": user?.can("View-Pre-Recovery-Room"),
			"sala-de-recuperacao": user?.can("View-Recovery-Room"),
		};

		const pathSafeSurgery = Object.keys(paths).find((path) => paths[path] === true);

		return pathSafeSurgery;
	}

	const operatingRoomPermissions = [
		"View-Surgical-Nursing-Act",
		"View-Surgical-Medical-Act",
		"View-Surgical-Anesthetic-Act",
	];

	const hasOperatingRoomPermissions = operatingRoomPermissions.some((permission) =>
		currentAuth?.user.can(permission as SystemPermissions)
	);

	return (
		<Card className="mb-10">
			<Card.Body className="pt-9 pb-0">
				<Row className="d-flex flex-responsive gap-2">
					<Col
						md={skeleton ? 1 : "auto"}
						sm={3}
						className={classNames("pe-3", !skeleton && "ps-0")}
					>
						{skeleton ? (
							<SkeletonWrapper height={60} width={70} />
						) : (
							<img
								className="patient-image"
								src={attendance.image || toAbsoluteUrl("/media/avatars/blank.png")}
								alt="Imagem do Paciente"
							/>
						)}
					</Col>

					<Col
						lg={width < 1335 ? "auto" : 4}
						md={9}
						className="d-flex flex-column patient-information p-0"
					>
						{skeleton ? (
							<SkeletonWrapper />
						) : (
							<div className="align-items-center text-gray-800 text-hover-primary fs-4 fw-bolder w-fit-content">
								{attendance.patientName}
							</div>
						)}

						{skeleton ? (
							<SkeletonWrapper count={2} />
						) : (
							<div className="d-flex flex-column fw-bold fs-6">
								<span className="text-gray-700 text-hover-primary w-fit-content">
									Procedimento: {surgicalAttendance?.expertise?.procedure.name}
								</span>

								<span className="text-gray-700 text-hover-primary w-fit-content">
									Data do Procedimento:{" "}
									{dayjs(surgicalAttendance?.surgeryDate).format("DD/MM/YYYY")}
								</span>
							</div>
						)}
					</Col>

					<Col
						lg={width < 1335 && 12}
						className={classNames(
							"d-flex",
							width > 1335 ? "justify-content-end" : "my-5",
							width > 768 ? "gap-2-75rem" : "flex-column gap-3"
						)}
					>
						<Col
							className="d-flex flex-column gap-3"
							md={skeleton ? 2 : "auto"}
							sm={5}
							xs={12}
						>
							<VitalSignsCard
								icon={
									<KTSVG
										path="/media/icons/vital-signs/blood-pressure.svg"
										className="svg-icon-2"
									/>
								}
								label="Pressão Arterial"
								value={`${latestMetrics?.systolicBloodPressure} x ${latestMetrics?.diastolicBloodPressure}`}
								skeleton={skeleton}
							/>

							<VitalSignsCard
								icon={
									<KTSVG
										path="/media/icons/vital-signs/saturation.svg"
										className="svg-icon-2"
									/>
								}
								label="Saturação"
								value={`${latestMetrics?.oxygenSaturation}% SpO2`}
								skeleton={skeleton}
							/>

							<VitalSignsCard
								icon={
									<KTSVG
										path="/media/icons/vital-signs/temperature.svg"
										className="svg-icon-2"
									/>
								}
								label="Temperatura Corporal"
								value={latestMetrics?.temperature}
								skeleton={skeleton}
							/>
						</Col>

						<Col
							className="d-flex flex-column gap-3"
							md={skeleton ? 2 : "auto"}
							sm={5}
							xs={12}
						>
							<VitalSignsCard
								icon={
									<KTSVG
										path="/media/icons/vital-signs/heart-rate.svg"
										className="svg-icon-2"
									/>
								}
								label="Freq. Cardíaca"
								value={latestMetrics?.heartRate}
								skeleton={skeleton}
							/>

							<VitalSignsCard
								icon={
									<KTSVG
										path="/media/icons/vital-signs/respiration-frequency.svg"
										className="svg-icon-2"
									/>
								}
								label="Freq. Respiratória"
								value={latestMetrics?.respiratoryFrequency}
								skeleton={skeleton}
							/>

							<VitalSignsCard
								icon={
									<KTSVG
										path="/media/icons/vital-signs/blood-glucose.svg"
										className="svg-icon-2"
									/>
								}
								label="Glicemia"
								value={latestMetrics?.bloodGlucose}
								skeleton={skeleton}
							/>
						</Col>

						<Col md={skeleton ? 2 : "auto"} className="patient-alerts">
							{skeleton ? (
								<SkeletonWrapper className="mb-4" />
							) : (
								<div className="d-flex align-items-center gap-2 mb-3">
									<BsBell className="fs-1" />

									<span className="text-gray-800 text-hover-primary fs-5 fw-bolder">
										Avisos
									</span>
								</div>
							)}

							{skeleton && <SkeletonWrapper />}

							{!skeleton && !hasFilledHospitalizationPermit && (
								<div className="d-flex flex-column">
									<span className="align-items-center badge badge-light-warning w-fit-content fs-8 mb-2 gap-2">
										AIH não preenchida
										<Link to={`/informacoes-aih/boletim/${attendance.id}`}>
											<IoMdOpen className="text-warning text-hover-primary" />
										</Link>
									</span>
								</div>
							)}
						</Col>
					</Col>
				</Row>

				<Row>
					<ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap">
						<ScrollableTabs>
							<TabsWithRoute
								name="Pré-Operatório"
								route={`/atendimento/${attendance.id}/pre-operatorio/admissao-de-enfermagem`}
								secondOptionActive={activeTab === "before-operatory"}
							/>

							{hasOperatingRoomPermissions && (
								<TabsWithRoute
									name="S.O."
									route={`/atendimento/${attendance.id}/sala-de-operacao/ato-cirurgico-de-enfermagem`}
									secondOptionActive={activeTab === "operating-room"}
								/>
							)}

							<TabsWithRoute
								name="S.R.P.A"
								route={`/atendimento/${attendance.id}/sala-de-recuperacao-pos-anestesica`}
								secondOptionActive={activeTab === "post-anesthetic-recovery-room"}
							/>

							<TabsWithRoute
								name="Cirurgia Segura"
								route={`/atendimento/${attendance.id}/cirurgia-segura/${pathSafeSurgery}`}
								secondOptionActive={activeTab === "safe-surgery"}
							/>
						</ScrollableTabs>
					</ul>
				</Row>
			</Card.Body>
		</Card>
	);
}
